import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Ticket, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { label: "100 % (kostenlos)", percent_off: 100 },
  { label: "50 %", percent_off: 50 },
  { label: "25 %", percent_off: 25 },
  { label: "10 %", percent_off: 10 },
];

export default function AdminCoupons() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "", percent_off: 100, duration: "forever",
    duration_in_months: "", max_redemptions: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/billing/admin/coupons`, { withCredentials: true });
      setList(r.data?.coupons || []);
    } catch (e) {
      toast.error("Rabattcodes konnten nicht geladen werden", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const codeClean = form.code.trim().toUpperCase();
    if (!codeClean) { toast.error("Bitte einen Code eingeben."); return; }
    if (codeClean.length < 3) {
      toast.error("Code zu kurz", { description: "Mindestens 3 Zeichen." });
      return;
    }
    if (!/^[A-Z0-9_-]+$/.test(codeClean)) {
      toast.error("Ungültige Zeichen im Code", {
        description: "Erlaubt sind nur Buchstaben (A–Z), Ziffern (0–9), Bindestrich und Unterstrich. Keine Leerzeichen, Umlaute, Punkte oder %-Zeichen.",
      });
      return;
    }
    setCreating(true);
    try {
      const payload = {
        code: codeClean,
        percent_off: Number(form.percent_off),
        duration: form.duration,
      };
      if (form.duration === "repeating" && form.duration_in_months) {
        payload.duration_in_months = Number(form.duration_in_months);
      }
      if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
      await axios.post(`${API}/api/billing/admin/coupons`, payload, { withCredentials: true });
      toast.success(`Rabattcode ${payload.code} angelegt`);
      setForm({ ...form, code: "", max_redemptions: "" });
      load();
    } catch (err) {
      // FastAPI 422 = Pydantic-Fehler-Liste in detail. Alles andere = String/dict.
      const raw = err?.response?.data?.detail;
      let msg;
      if (Array.isArray(raw)) {
        msg = raw.map((it) => {
          const field = Array.isArray(it.loc) ? it.loc[it.loc.length - 1] : it.loc;
          const labels = { code: "Code", percent_off: "Rabatt %", duration: "Gültigkeit",
                           duration_in_months: "Monate", max_redemptions: "Max. Einlösungen" };
          return `${labels[field] || field}: ${it.msg}`;
        }).join(" · ");
      } else if (typeof raw === "string") {
        msg = raw;
      } else if (raw && typeof raw === "object") {
        msg = raw.message || raw.detail || JSON.stringify(raw);
      } else {
        msg = err?.message || "Unbekannter Fehler.";
      }
      toast.error("Anlegen fehlgeschlagen", { description: msg });
      try {
        document.body.style.pointerEvents = "";
        document.body.style.removeProperty("pointer-events");
        document.body.removeAttribute("data-scroll-locked");
      } catch {}
    } finally { setCreating(false); }
  };

  const deactivate = async (id, code) => {
    if (!window.confirm(`Rabattcode ${code} deaktivieren? Bereits eingelöste Abos bleiben unberührt.`)) return;
    try {
      await axios.delete(`${API}/api/billing/admin/coupons/${id}`, { withCredentials: true });
      toast.success(`${code} deaktiviert`);
      load();
    } catch (e) {
      toast.error("Deaktivieren fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Rabattcodes"
        description="Legt Aktions-Codes an, die Nutzer:innen im Stripe-Checkout eingeben können. Alle Codes laufen sofort über Stripe – bereits eingelöste Abos bleiben bei Deaktivierung erhalten."
        testid="admin-coupons-header"
      />

      <section className="card-surface p-5 md:p-6 mb-6" data-testid="admin-coupon-form">
        <h3 className="font-heading text-lg mb-4">Neuen Code anlegen</h3>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Code</Label>
              <Input value={form.code} onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
                placeholder="z. B. LAUNCH50" className="mt-1 font-mono uppercase" data-testid="coupon-code" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Nur A–Z, 0–9, Bindestrich, Unterstrich. Keine Leerzeichen, Umlaute oder %-Zeichen.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rabatt in %</Label>
              <Select value={String(form.percent_off)} onValueChange={(v) => setForm((s) => ({ ...s, percent_off: Number(v) }))}>
                <SelectTrigger className="mt-1" data-testid="coupon-percent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => <SelectItem key={p.percent_off} value={String(p.percent_off)}>{p.label}</SelectItem>)}
                  <SelectItem value="75">75 %</SelectItem>
                  <SelectItem value="33">33 %</SelectItem>
                  <SelectItem value="15">15 %</SelectItem>
                  <SelectItem value="5">5 %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Gültigkeit</Label>
              <Select value={form.duration} onValueChange={(v) => setForm((s) => ({ ...s, duration: v }))}>
                <SelectTrigger className="mt-1" data-testid="coupon-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="forever">Für immer (dauerhafter Rabatt)</SelectItem>
                  <SelectItem value="once">Einmalig (erste Zahlung)</SelectItem>
                  <SelectItem value="repeating">Für X Monate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.duration === "repeating" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Anzahl Monate</Label>
                <Input type="number" min={1} max={36}
                  value={form.duration_in_months}
                  onChange={(e) => setForm((s) => ({ ...s, duration_in_months: e.target.value }))}
                  className="mt-1" data-testid="coupon-months" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Max. Einlösungen (optional)</Label>
              <Input type="number" min={1}
                value={form.max_redemptions}
                onChange={(e) => setForm((s) => ({ ...s, max_redemptions: e.target.value }))}
                placeholder="unbegrenzt" className="mt-1" data-testid="coupon-max" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Tipp:</strong> 100 % + „Für immer" = dauerhaft kostenloser Zugang (Test-Nutzer:innen, Partner). 50 % + „Einmalig" = klassische Launch-Aktion.
          </p>
          <Button type="submit" className="rounded-full" disabled={creating} data-testid="coupon-submit">
            {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lege an …</> : <><Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Code anlegen</>}
          </Button>
        </form>
      </section>

      <section data-testid="admin-coupon-list">
        <h3 className="font-heading text-lg mb-3">Aktive Codes</h3>
        {loading ? (
          <div className="card-surface p-6 text-sm text-muted-foreground text-center">Wird geladen …</div>
        ) : list.length === 0 ? (
          <div className="card-surface p-8 text-center">
            <Ticket className="h-8 w-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">Noch keine aktiven Rabattcodes.</p>
          </div>
        ) : (
          <div className="card-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Rabatt</th>
                  <th className="px-4 py-3">Gültigkeit</th>
                  <th className="px-4 py-3">Einlösungen</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-border" data-testid={`coupon-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono">{c.code}</td>
                    <td className="px-4 py-3">
                      <Badge className="rounded-full bg-primary/15 text-foreground border border-primary/30">
                        {c.percent_off} %
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {c.duration === "forever" && "Für immer"}
                      {c.duration === "once" && "Einmalig"}
                      {c.duration === "repeating" && `${c.duration_in_months} Monate`}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">
                      {c.times_redeemed} {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" className="rounded-full h-8" onClick={() => deactivate(c.id, c.code)} data-testid={`coupon-deactivate-${c.id}`}>
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> Deaktivieren
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
